// Upgrade modal — opens app-wide via: window.dispatchEvent(new CustomEvent("ift:upgrade-required", { detail: { message } }))
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { X, Check, TreePine } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Emails that ALWAYS bypass the upgrade paywall on the client. Backend
// mirror lives at backend/.env `SUPER_ADMIN_EMAILS`. Kept in sync
// manually — this is a belt-and-suspenders in case the backend returns
// a stale 402 before the user's platform_role has propagated to the
// current session.
const SUPER_ADMIN_EMAILS = new Set([
  "tomvell@gmail.com",
  "bijujoe@gmail.com",
  "sajijoseph@gmail.com",
  "sibyvell@gmail.com",
  "worldofjoyinfo@gmail.com",
  "admin@worldofjoy.app",
]);

function isPlatformSuperAdmin(user) {
  if (!user) return false;
  if (user.is_admin || user.is_super_admin) return true;
  const role = (user.platform_role || "").toLowerCase();
  if (["super_admin", "admin", "organization_admin"].includes(role)) return true;
  const email = (user.email || "").toLowerCase().trim();
  return !!email && SUPER_ADMIN_EMAILS.has(email);
}

export default function UpgradeModal() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [plans, setPlans] = useState([]);
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    const onNeed = (e) => {
      // Super admins (Biju / Tom / Saji / staff) never see paywalls,
      // regardless of what the server or client legacy code says.
      if (isPlatformSuperAdmin(user)) return;
      setMessage(e.detail?.message || "You've reached your plan's family member limit.");
      setOpen(true);
      http.get("/pricing/plans").then((r) => setPlans((r.data.plans || []).filter((p) => p.key !== "free"))).catch(() => {});
      http.get("/pricing/upgrade-options").then((r) => setOpts(r.data)).catch(() => setOpts(null));
    };
    window.addEventListener("ift:upgrade-required", onNeed);
    return () => window.removeEventListener("ift:upgrade-required", onNeed);
  }, [user]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 overflow-y-auto" data-testid="upgrade-modal">
      <div className="max-w-2xl w-full rounded-2xl bg-white p-6 shadow-2xl my-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#B25D38" }}>Grow your family tree</div>
            <h3 className="font-display text-2xl mt-1">Your tree is ready for more</h3>
            <p className="text-sm text-black/70 mt-1" data-testid="upgrade-modal-message">{message}</p>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-black/5" data-testid="upgrade-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          {plans.map((p) => {
            const o = opts?.options?.find((x) => x.key === p.key);
            if (o?.relation === "included") return null;
            const isCurrent = o?.relation === "current";
            const base = o?.is_upgrade ? o.base_paise : p.price_paise;
            const total = o ? o.total_paise : (p.total_paise ?? base + Math.round(base * 0.18));
            return (
              <div key={p.key} className="rounded-xl border p-4" style={{ borderColor: isCurrent ? "#16A34A" : p.key === "pro" ? "#EA580C" : "#E8DFCF" }} data-testid={`upgrade-plan-${p.key}`}>
                {isCurrent ? (
                  <div className="text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: "#16A34A" }}>Your current plan</div>
                ) : p.key === "pro" ? (
                  <div className="text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: "#EA580C" }}>Most popular</div>
                ) : null}
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="font-display text-2xl mt-1">₹{(base / 100).toLocaleString("en-IN")}<span className="text-[10px] font-sans opacity-60"> + GST /year</span></div>
                <div className="text-[11px] opacity-60">₹{(total / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })} total{o?.is_upgrade ? " — pay only the difference" : ""}</div>
                <div className="mt-2 text-xs inline-flex items-center gap-1">
                  <TreePine className="w-3.5 h-3.5" style={{ color: "#16A34A" }} />
                  {p.node_limit ? `Up to ${p.node_limit} members` : "Unlimited members"}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs inline-flex items-center gap-1 opacity-70"><Check className="w-3.5 h-3.5" /> UPI · Cards · Netbanking via Razorpay</span>
          <Link to="/pricing" onClick={() => setOpen(false)} className="wofy-btn-primary" data-testid="upgrade-modal-cta">
            Compare plans & upgrade
          </Link>
        </div>
      </div>
    </div>
  );
}
